import { CButton, CTag, CText } from "@carrefour/design-system-components-react";
import "reactjs-popup/dist/index.css";
import styled from "styled-components";
import { AlertStatus, formatDateTimeToDate, getLastAlertDetail } from "../../utils/alertUtils";
import { AlertSerie } from "../../types/alertSerie";
import { useEffect, useState, useMemo } from "react";
import { formatCurrency } from "../../utils/numberUtils";
import Banner from "../Banner/Banner";
import { t } from "i18next";

const FINANCIAL_IMPACT_CHANGE_DATE = window?.config?.FINANCIAL_IMPACT_CHANGE_DATE;
const isAfterChangeDate = FINANCIAL_IMPACT_CHANGE_DATE ? new Date() >= new Date(FINANCIAL_IMPACT_CHANGE_DATE) : false;

type AlertSidePanelProps = {
  groupRefChange: (ref: string, totalImpact: number, groupIcon: string) => void;
  goToAlers: () => void;
  importedDate: string;
  alertSeries?: AlertSerie[];
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const CButtonCustom = styled(CButton)`
  i {
    color: black;
  }
`;

const GreyTag = styled(CTag)`
  background-color: #E6E6E6 !important;
  color: #767575 !important;
  border: none !important;
`;

const CTagNoBorder = styled(CTag)`
  border: none !important;
`;

const GroupRowContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  background-color: ${props => props.isSelected ? "#E6F1FE" : "transparent"};
  border-radius: 8px;
  padding: .3rem .5rem;
  transition: background-color 0.2s ease;
  cursor: pointer;
`;

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Summary = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 1;
  background: white;
`;

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding-bottom: 1rem;
`;

const BottomBanner = styled.div`
  margin-top: auto;
`;

const SummaryCard = styled.div`
  flex: 1;
  background: #f7f7f7;
  border-radius: 12px;
  padding: 0.75rem;
  text-align: center;
`;

const ListRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  border-top: 1px solid #eee;
`;

const Left = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  align-items: flex-start;
`;

const Right = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
  align-items: flex-end;
`;

const FooterText = styled(CText)`
  text-align: left;
  margin-left: 1rem;
  margin-bottom: 1rem;
  margin-top: 1rem;
`;

const iconMap: Record<string, string> = {
  'E-commerce': 'mouse-pointer',
  'Détention & Stocks': 'intercom',
  'Client': 'user',
  'Commerce': 'shopping-cart',
  'Productivité': 'target',
  'Démarque': 'products'
};

// Function to get icon based on group name
const getGroupIcon = (groupName: string): string => {
  return iconMap[groupName] || '';
};

const AlertSidePanel: React.FC<AlertSidePanelProps> = ({
  importedDate,
  groupRefChange,
  goToAlers,
  alertSeries
}) => {

  // Track selected group
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  
  // Extract kpiGroupNames with their total impact, alert series count, and status counts
  const groupData = useMemo(() => {
    if (!alertSeries) return [];
    
    const groupMap = new Map<string, { 
      impact: number; 
      count: number;
      resolved: number;
      inProgress: number;
      requiredAction: number;
      hasMagasinSector: boolean;
    }>();

    if(!isAfterChangeDate) {
      // FIRST PASS: Identify groups that contain "magasin" sector
      alertSeries.forEach((serie: AlertSerie) => {
        const groupName = getLastAlertDetail(serie)?.kpiGroupName || "";
        const sector = getLastAlertDetail(serie)?.sector;

        if (groupName) {
          const existing = groupMap.get(groupName) || { 
            impact: 0, 
            count: 0,
            resolved: 0,
            inProgress: 0,
            requiredAction: 0,
            hasMagasinSector: false
          };

          // Check if this group has "Magasin" sector
          if (sector === "magasin" && serie.status !== AlertStatus.RESOLVED) {
            existing.hasMagasinSector = true;
          }
          
          groupMap.set(groupName, existing);
        }
      });
      
      // SECOND PASS: Calculate impacts and counts based on the identified group types
      alertSeries.forEach(serie => {
        const alertDetail = getLastAlertDetail(serie);
        const groupName = alertDetail?.kpiGroupName || "";
        const sector = alertDetail?.sector;

        if (groupName) {
          const existing = groupMap.get(groupName)!; // We know it exists from first pass
          
          // For groups that have "Magasin" sector, only count impact from "Magasin" series
          // For other groups, count all impacts
          const shouldCountImpact = !existing.hasMagasinSector || sector === "magasin";
          
          // Count by status
          let resolved = existing.resolved;
          let inProgress = existing.inProgress;
          let requiredAction = existing.requiredAction;
          
          switch(serie.status) {
            case AlertStatus.RESOLVED:
              resolved++;
              break;
            case AlertStatus.INPROGRESS:
              inProgress++;
              break;
            case AlertStatus.TOADJUST:
            case AlertStatus.NEW:
            case AlertStatus.IGNORED:
              requiredAction++;
              break;
          }
          
          groupMap.set(groupName, {
            impact: existing.impact + (shouldCountImpact && serie.status !== AlertStatus.RESOLVED ? (serie.impactFinancier || 0) : 0),
            count: existing.count + 1,
            resolved,
            inProgress,
            requiredAction,
            hasMagasinSector: existing.hasMagasinSector
          });
        }
      });
    } else {
      const groupedSeries = new Map<string, Map<string, { serie: AlertSerie; isMainKpi: boolean }[]>>();

      alertSeries.forEach((serie: AlertSerie) => {
        const alertDetail = getLastAlertDetail(serie);
        const groupName = alertDetail?.kpiGroupName || "";
        const category = alertDetail?.category || "";
        if (!groupName) return;

        const byCategory = groupedSeries.get(groupName) || new Map<string, { serie: AlertSerie; isMainKpi: boolean }[]>();
        const categorySeries = byCategory.get(category) || [];

        categorySeries.push({ serie, isMainKpi: alertDetail?.isMainKpi === 1 });
        byCategory.set(category, categorySeries);
        groupedSeries.set(groupName, byCategory);
      });

      groupedSeries.forEach((byCategory, groupName) => {
        const aggregated = {
          impact: 0,
          count: 0,
          resolved: 0,
          inProgress: 0,
          requiredAction: 0,
          hasMagasinSector: false
        };

        byCategory.forEach((categorySeries) => {
          const hasMainKpi = categorySeries.some((item) => item.isMainKpi && item.serie.status !== AlertStatus.RESOLVED);

          categorySeries.forEach(({ serie }) => {
            switch (serie.status) {
              case AlertStatus.RESOLVED:
                aggregated.resolved++;
                break;
              case AlertStatus.INPROGRESS:
                aggregated.inProgress++;
                break;
              case AlertStatus.TOADJUST:
              case AlertStatus.NEW:
              case AlertStatus.IGNORED:
                aggregated.requiredAction++;
                break;
            }
            const alertDetail = getLastAlertDetail(serie);
            if (hasMainKpi && alertDetail?.isMainKpi === 1) {
              aggregated.impact += serie.impactFinancier || 0;
            } else if (!hasMainKpi && alertDetail?.isMainKpi === 0 && serie.status !== AlertStatus.RESOLVED) {
              aggregated.impact += serie.impactFinancier || 0;
            }

            aggregated.count++;
          });
        });

        groupMap.set(groupName, aggregated);
      });
    }
    
    return Array.from(groupMap.entries()).map(([name, data]) => {
      // Determine priority: Resolved > In Progress > Required Action
      let tagStatus: "success" | "info" | "error";
      let displayText: string;
      let statusPriority: number;
      
      if (data.requiredAction > 0) {
        tagStatus = "error";
        displayText = data.requiredAction === 1 
          ? `${data.requiredAction} action requise`
          : `${data.requiredAction} actions requises`;
        statusPriority = 1;
      } else if (data.inProgress > 0) {
        tagStatus = "info";
        displayText = data.inProgress === 1 
          ? `${data.inProgress} action en cours`
          : `${data.inProgress} actions en cours`;
        statusPriority = 2;
      } else {
        tagStatus = "success";
        displayText = data.resolved === 1 
          ? `${data.resolved} alerte résolue`
          : `${data.resolved} alertes résolues`;
        statusPriority = 3;
      }
      
      return {
        name,
        totalImpact: data.impact,
        alertCount: data.count,
        tagStatus,
        displayText,
        statusPriority
      };
    }).sort((a, b) => {
      // 1. First sort by status priority (1=required, 2=progress, 3=resolved)
      if (a.statusPriority !== b.statusPriority) {
        return a.statusPriority - b.statusPriority;
      }
      
      // 2. Then sort by financial impact (descending)
      if (a.totalImpact !== b.totalImpact) {
        return b.totalImpact - a.totalImpact;
      }
      
      // 3. Finally sort by alert count (descending)
      return b.alertCount - a.alertCount;
    });
  }, [alertSeries]);

  // Calculate total impact across all groups
  const getTotalImpact = (): string => {
    const total = groupData.reduce((sum, group) => sum + group.totalImpact, 0);
    return total > 0 ? formatCurrency(total) : "Non chiffré";
  };

  // Get total alert series count
  const getTotalProcessedAlert = (): number => {
    if (!alertSeries) return 0;
    
    return alertSeries.filter(serie => 
      serie.status === AlertStatus.RESOLVED || 
      serie.status === AlertStatus.INPROGRESS
    ).length;
  };

  const handleGroupClick = (groupName: string, totalImpact: number) => {
    setSelectedGroup(groupName);
    goToAlers();
    groupRefChange(groupName, totalImpact, getGroupIcon(groupName));
  };

    useEffect(() => {
    if (groupData.length > 0 && !selectedGroup) {
      const firstGroup = groupData[0];
      setSelectedGroup(firstGroup.name);
      groupRefChange(firstGroup.name, firstGroup.totalImpact, getGroupIcon(firstGroup.name));
    }
  }, [groupData, selectedGroup, groupRefChange]);

  return (
    <PanelContainer>
      <Summary>
        <SummaryCard>
          <CText>Impact total</CText>
          <CText bold>{getTotalImpact()}</CText>
        </SummaryCard>
        <SummaryCard>
          <CText>Alertes traitées</CText>
          <CText style={{ color: "#2563eb" }} bold>
            {getTotalProcessedAlert()}/{alertSeries?.length || 0 } {(alertSeries?.length || 0) === 1 ? 'alerte' : 'alertes'}
          </CText>
        </SummaryCard>
      </Summary>

      <ScrollContent>
        {groupData.map((group) => (
          <ListRow key={group.name}>
            <GroupRowContainer 
              isSelected={selectedGroup === group.name}
              onClick={() => {
                goToAlers(); 
                handleGroupClick(group.name, group.totalImpact);
              }}
            >
              <Container> 
                <div style={{width: "50px", marginRight:"4px", backgroundColor: "#f7f7f7", borderRadius: "5px"}}>
                  <CButtonCustom variation="tertiary" size="m" disabled appendIcon={getGroupIcon(group.name)} />
                </div>
                <Left>
                  <CText size="s" bold>{group.name}</CText>
                  {group.totalImpact > 0 ? (
                    <CTagNoBorder size="s" status={group.tagStatus}>
                      {formatCurrency(group.totalImpact)}
                    </CTagNoBorder>
                  ) : (
                    <GreyTag size="s">
                      Non chiffré
                    </GreyTag>
                  )}
                </Left>
              </Container>
              <Container> 
                <Right>
                  <CText size={"s"} bold style={{ color: "#696969" }}>
                    {group.alertCount === 1 ? `${group.alertCount} alerte` : `${group.alertCount} alertes`}
                  </CText>
                  <CTagNoBorder size="s" status={group.tagStatus}>{group.displayText}</CTagNoBorder>
                </Right>
                <CButtonCustom 
                  variation="tertiary" 
                  size="m" 
                  appendIcon="chevron-right" 
                />
              </Container>
            </GroupRowContainer>
          </ListRow>
        ))}
        
        <div style={{borderTop: "1px solid #eee"}}></div>
        <FooterText>
          <CText size={"s"} bold>Données mises à jour {formatDateTimeToDate(importedDate)}</CText>
        </FooterText>
      </ScrollContent>
      <BottomBanner>
        <Banner
          data={{ id: "alert-banner", 
            title: t('alerts.help.alertHelpTitle'), 
            text: t('alerts.help.alertHelpText'),
            link: "https://www.appsheet.com/start/203e014a-1b7c-4b8a-9ad9-eb13451fa82a?platform=desktop#appName=CoachOLib-530718602&vss=H4sIAAAAAAAAA6WOwQrCMBBE_2XO-YJcpaCIIiqCGA-x2UKwJqVJ1RLy726r4rl43Le8mUm4W3rsoi6vkKf0u5bUQyIp7PuGFKTCzLvY-lpBKKz17Q3nx02xVcjIZ_GVIwXINMGVf_QKWEMu2spSOwQNGgd8JH4PCoNRQBa4dVFfahp3spAzs8qXXSBz4BFTy8PCFc9GO7PyhvMqXQfKL0pLITtWAQAA&view=HYPER" }}
          icon="help"
          type="INFO"
          isCloseable={false}         
          />
      </BottomBanner>
    </PanelContainer>
  );
};

export default AlertSidePanel;